// pages/master/customer/MasterCustomerAddEdit.jsx
'use client';

import { useState, useEffect } from 'react';
import { Box, Stack, TextField, CircularProgress, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

import { createCustomer, updateCustomer, getCustomerById } from '../../../stores/customerStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useCountryList } from '../../../stores/countryStore';
import { useBusinessTypeList } from '../../../stores/businessTypeStore';
import MasterCountryAddEdit from '../country/MasterCountryAddEdit';
import BusinessTypeAddEdit from '../business-type/BusinessTypeAddEdit';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const MasterCustomerAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: null,
    company_name: '',
    country: null,
    country_name: '',
    business_type: null,
    business_type_name: '',
    name: '',
    address: '',
    code: '',
    cp_name_1: '',
    phone: '',
    fax: '',
    website: '',
    npwp_no: '',
    npwp_address: ''
  });

  const [loading, setLoading] = useState(false);

  // ---- Fetch internal lookups ----
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const countryPickerParams = usePickerPagination({
    fetchHook: useCountryList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const businessTypePickerParams = usePickerPagination({
    fetchHook: useBusinessTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const companyOptions = companyPickerParams.options;
  const countryOptions = countryPickerParams.options;
  const businessTypeOptions = businessTypePickerParams.options;
  // ---- end lookups ----

  // Fetch data saat Edit mode
  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getCustomerById(id);
        if (!mounted) return;
        if (data) {
          const companyVal = data.company ?? data.company_id ?? null;
          const countryVal = data.country ?? data.country_id ?? null;
          const businessTypeVal = data.business_type ?? data.business_type_id ?? null;

          setInitialValues((prev) => ({
            ...prev,
            company: companyVal != null ? String(companyVal) : prev.company,
            company_name: data.company_name ?? data.join?.company_name ?? prev.company_name,
            country: countryVal != null ? String(countryVal) : prev.country,
            country_name: data.country_name ?? data.join?.country_name ?? prev.country_name,
            business_type: businessTypeVal != null ? String(businessTypeVal) : prev.business_type,
            business_type_name: data.business_type_name ?? data.join?.business_type_name ?? prev.business_type_name,
            name: data.name ?? prev.name,
            address: data.address ?? prev.address,
            code: data.code ?? prev.code,
            cp_name_1: data.cp_name_1 ?? prev.cp_name_1,
            phone: data.phone ?? prev.phone,
            fax: data.fax ?? prev.fax,
            website: data.website ?? prev.website,
            npwp_no: data.npwp_no ?? prev.npwp_no,
            npwp_address: data.npwp_address ?? prev.npwp_address
          }));
        }
      } catch (err) {
        console.error('Failed to load customer:', err);
        notify({ message: t('customer.failedLoadData', 'Failed to load customer data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  // Inisialisasi company dari localStorage ketika mode Add
  useEffect(() => {
    if (id || !open) return;
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (!stored) return;

    if (companyOptions && companyOptions.length) {
      const match = companyOptions.find((c) => String(c.value) === String(stored));
      if (match) {
        setInitialValues((prev) => {
          if (prev.company) return prev;
          return {
            ...prev,
            company: String(match.value),
            company_name: match.label ?? match.name ?? String(match.value)
          };
        });
        return;
      }
    }

    setInitialValues((prev) => {
      if (prev.company) return prev;
      return {
        ...prev,
        company: String(stored),
        company_name: String(stored)
      };
    });
  }, [companyOptions, id, open]);

  // Validasi formik
  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    cp_name_1: Yup.string().max(20, 'PIC Name maximum 20 characters')
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (values.company == null || values.company === '') throw new Error('Company tidak valid');

        const numericOrNull = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        };

        const payload = {
          id: id ?? 0,
          company: Number(values.company),
          country: numericOrNull(values.country),
          business_type: numericOrNull(values.business_type),
          name: formatUpperCase(values.name) ?? '',
          address: values.address ?? '',
          code: values.code ?? '',
          cp_name_1: values.cp_name_1 ?? '',
          phone: values.phone ?? '',
          fax: values.fax ?? '',
          website: values.website ?? '',
          npwp_no: values.npwp_no ?? '',
          npwp_address: values.npwp_address ?? ''
        };

        if (id) {
          await updateCustomer(id, payload);
          notify({ message: t('customer.updateSuccess', 'Customer updated successfully'), severity: 'success' });
        } else {
          await createCustomer(payload);
          notify({ message: t('customer.createSuccess', 'Customer added successfully'), severity: 'success' });
        }

        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button
        onClick={onClose}
        disabled={formik.isSubmitting}
        sx={{
          color: 'text.secondary',
          textTransform: 'none'
        }}
      >
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none' }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('masterCustomer.title', 'Customer')
          : t('general.add', 'Add') + ' ' + t('masterCustomer.title', 'Customer')
      }
      maxWidth="sm"
      fullScreenMode={false} // Mode standar modal
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5}>
              <PickerField
                label={t('general.company', 'Company') + ' *'}
                value={formik.values.company}
                {...companyPickerParams}
                options={companyOptions}
                allowAdd
                AddComponent={MasterCompanyAddEdit}
                onChange={(opt) => {
                  const val = typeof opt === 'object' ? opt?.value : opt;
                  const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                  const idVal = selected ? selected.value : (val ?? null);
                  formik.setFieldValue('company', idVal);
                  if (selected) formik.setFieldValue('company_name', selected.label);
                }}
                dialogTitle={t('masterCustomer.selectCompany', 'Select Company')}
                error={formik.touched.company && !!formik.errors.company}
                helperText={formik.touched.company && formik.errors.company}
              />

              <TextField
                label={t('customer.name', 'Customer Name') + ' *'}
                name="name"
                value={formik.values.name ?? ''}
                onChange={(e) => {
                  formik.setFieldValue('name', formatUpperCase(e.target.value));
                }}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />

              <TextField
                label={t('customer.code', 'Customer Code') + ' *'}
                name="code"
                value={formik.values.code ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
              />

              <PickerField
                label={t('general.country', 'Country')}
                value={formik.values.country}
                {...countryPickerParams}
                options={countryOptions}
                onChange={(opt) => {
                  const val = typeof opt === 'object' ? opt?.value : opt;
                  const selected = countryOptions.find((c) => String(c.value) === String(val)) ?? null;
                  const idVal = selected ? selected.value : (val ?? null);
                  formik.setFieldValue('country', idVal);
                  if (selected) formik.setFieldValue('country_name', selected.label);
                }}
                allowAdd
                AddComponent={MasterCountryAddEdit}
                dialogTitle={t('masterCustomer.selectCountry', 'Select Country')}
                disabled={!formik.values.company}
                helperText={!formik.values.company ? t('general.selectCompanyFirst', '* Please select a company first') : ''}
              />

              <PickerField
                label={t('masterCustomer.businessType', 'Customer Type')}
                value={formik.values.business_type}
                {...businessTypePickerParams}
                options={businessTypeOptions}
                onChange={(opt) => {
                  const val = typeof opt === 'object' ? opt?.value : opt;
                  const selected = businessTypeOptions.find((c) => String(c.value) === String(val)) ?? null;
                  const idVal = selected ? selected.value : (val ?? null);
                  formik.setFieldValue('business_type', idVal);
                  if (selected) formik.setFieldValue('business_type_name', selected.label);
                }}
                allowAdd
                AddComponent={BusinessTypeAddEdit}
                dialogTitle={t('masterCustomer.selectCustomerType', 'Select Customer Type')}
              />

              <TextField
                label={t('company.address', 'Address')}
                name="address"
                value={formik.values.address ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
                multiline
                rows={2}
              />

              <TextField
                label={t('company.picName', 'PIC Name')}
                name="cp_name_1"
                value={formik.values.cp_name_1 ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
                error={formik.touched.cp_name_1 && Boolean(formik.errors.cp_name_1)}
                helperText={formik.touched.cp_name_1 && formik.errors.cp_name_1}
              />

              <TextField
                label={t('customer.phone', 'Customer Phone')}
                name="phone"
                value={formik.values.phone ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
              />

              <TextField
                label={t('masterCustomer.fax', 'Fax')}
                name="fax"
                value={formik.values.fax ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
              />

              <TextField
                label={t('masterCustomer.website', 'Website')}
                name="website"
                value={formik.values.website ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
              />

              <TextField
                label={t('customer.npwpNo', 'NPWP Number')}
                name="npwp_no"
                value={formik.values.npwp_no ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
              />

              <TextField
                label={t('customer.npwpAddress', 'NPWP Address')}
                name="npwp_address"
                value={formik.values.npwp_address ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterCustomerAddEdit;
